import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

export interface RouteAccountStatementWeekExport {
	label: string;
	buses: string;
	facturacion: number;
	discounts: { name: string; amount: number }[];
	totalDiscounts: number;
	deposit: number;
	paid: number;
}

export interface RouteAccountStatementPartnerExport {
	nit: string;
	partnerName: string;
	weeks: RouteAccountStatementWeekExport[];
}

export interface RouteAccountStatementRouteExport {
	routeId: string;
	routeName: string;
	discountNames: string[];
	partners: RouteAccountStatementPartnerExport[];
}

@Injectable({
  	providedIn: 'root'
})
export class Excel {
  	constructor() { }

	private readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	private toUpperCasePreservingEmail(value: any): any {
		if(typeof value !== 'string') {
			return value;
		}

		const trimmedValue = value.trim();
		if(this.emailRegex.test(trimmedValue)) {
			return value;
		}

		return value.toUpperCase();
	}

	public exportDataToExcel(
		data: any[],
		displayHeaders: string[],
		dataKeys: string[],
		fileName: string,
		sheetName: string = 'Datos',
		detailsData?: any[][],
		detailsSheetName: string = 'Detalles',
		options?: {
			appendTotalsRow?: boolean;
			totalsKeys?: Record<string, 'sum'>;
			negativeRedKeys?: string[];
			currencyKeys?: string[];
			negativeRowFillKey?: string;
			negativeRowFillColor?: string;
			applyBlackBorders?: boolean;
			centerHeaders?: boolean;
			centerNumericValues?: boolean;
			leftAlignTextValues?: boolean;
			totalsLabel?: string;
			totalsLabelMergeSpan?: number;
			headerFillColor?: string;
			headerFontColor?: string;
			headerRowHeight?: number;
		}
	): void {
		const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
		const moneyFormat = '"Q" #,##0.00;-"Q" #,##0.00';
		const normalizedDisplayHeaders = displayHeaders.map(header => String(header || '').toUpperCase());
		
		const preparedData = data.map(row => {
			const newRow: { [key: string]: any } = {};

			dataKeys.forEach(key => {
				const value = row?.[key];
				newRow[key] = this.toUpperCasePreservingEmail(value);
			});

			return newRow;
		});

		const rows: any[] = [...preparedData];
		const totalsLabel = String(options?.totalsLabel || 'TOTAL').toUpperCase();
		const hasTotalsRow = options?.appendTotalsRow === true && !!options?.totalsKeys;

		if(hasTotalsRow && options?.totalsKeys) {
			const totalsRow: { [key: string]: any } = {};

			dataKeys.forEach((key, index) => {
				if(options.totalsKeys && options.totalsKeys[key] === 'sum') {
					const total = data.reduce((sum, row) => {
						const value = Number(row[key]);
						return sum + (isNaN(value) ? 0 : value);
					}, 0);
					totalsRow[key] = total;
				} else if(index === 0) {
					totalsRow[key] = totalsLabel;
				} else {
					totalsRow[key] = '';
				}
			});

			rows.push(totalsRow);
		}

		XLSX.utils.sheet_add_aoa(ws, [normalizedDisplayHeaders], { origin: 'A1' });
		XLSX.utils.sheet_add_json(ws, rows, { origin: 'A2', skipHeader: true, header: dataKeys });

		const colWidths = dataKeys.map((key, index) => ({
			wch: Math.max(
				normalizedDisplayHeaders[index] ? normalizedDisplayHeaders[index].length : 0,
				...rows.map(row => (row[key] ? String(row[key]).length : 0))
			) + 2
		}));

		ws['!cols'] = colWidths;

		const toColName = (index: number): string => {
			let col = '';
			let n = index + 1;
			while(n > 0) {
				const rem = (n - 1) % 26;
				col = String.fromCharCode(65 + rem) + col;
				n = Math.floor((n - 1) / 26);
			}
			return col;
		};

		const getCellRef = (rowIndexOneBased: number, colIndexZeroBased: number): string => `${toColName(colIndexZeroBased)}${rowIndexOneBased}`;
		const normalizeColor = (rawColor: string | undefined, fallback: string): string => {
			const clean = String(rawColor || '').replace('#', '').trim().toUpperCase();
			return /^[0-9A-F]{6}$/.test(clean) ? clean : fallback;
		};

		const shouldApplyTableStyle = options?.applyBlackBorders
			|| options?.centerHeaders
			|| options?.centerNumericValues
			|| options?.leftAlignTextValues
			|| (hasTotalsRow && Number(options?.totalsLabelMergeSpan) > 1);

		if(shouldApplyTableStyle) {
			const totalRowsWithHeader = rows.length + 1;
			const totalsRowOneBased = hasTotalsRow ? rows.length + 1 : -1;
			const totalsMergeSpan = Math.max(1, Math.min(Number(options?.totalsLabelMergeSpan) || 1, dataKeys.length));
			const headerFillColor = normalizeColor(options?.headerFillColor, '');
			const headerFontColor = normalizeColor(options?.headerFontColor, '000000');
			const blackBorder = {
				top: { style: 'thin', color: { rgb: '000000' } },
				bottom: { style: 'thin', color: { rgb: '000000' } },
				left: { style: 'thin', color: { rgb: '000000' } },
				right: { style: 'thin', color: { rgb: '000000' } }
			};

			for(let rowOneBased = 1; rowOneBased <= totalRowsWithHeader; rowOneBased++) {
				for(let colIndex = 0; colIndex < dataKeys.length; colIndex++) {
					const cellRef = getCellRef(rowOneBased, colIndex);
					if(!ws[cellRef]) {
						ws[cellRef] = { t: 's', v: '' };
					}

					const currentStyle = ws[cellRef].s || {};
					const nextStyle: any = { ...currentStyle };

					if(options?.applyBlackBorders) {
						nextStyle.border = blackBorder;
					}

					if(rowOneBased === 1 && options?.centerHeaders) {
						nextStyle.font = { ...(nextStyle.font || {}), bold: true, color: { rgb: headerFontColor } };
						if(headerFillColor) {
							nextStyle.fill = { patternType: 'solid', fgColor: { rgb: headerFillColor } };
						}
						nextStyle.alignment = { ...(nextStyle.alignment || {}), horizontal: 'center', vertical: 'center' };
					} else {
						const rowData = rows[rowOneBased - 2] || {};
						const cellValue = rowData[dataKeys[colIndex]];
						const isNumeric = typeof cellValue === 'number' && !isNaN(cellValue);
						const isTotalsLabelZone = hasTotalsRow
							&& rowOneBased === totalsRowOneBased
							&& colIndex < totalsMergeSpan;

						if(isTotalsLabelZone) {
							nextStyle.font = { ...(nextStyle.font || {}), bold: true };
							nextStyle.alignment = { ...(nextStyle.alignment || {}), horizontal: 'center', vertical: 'center' };
						} else if(isNumeric && options?.centerNumericValues) {
							nextStyle.alignment = { ...(nextStyle.alignment || {}), horizontal: 'center', vertical: 'center' };
						} else if(options?.leftAlignTextValues) {
							nextStyle.alignment = { ...(nextStyle.alignment || {}), horizontal: 'left', vertical: 'center' };
						}
					}

					ws[cellRef].s = nextStyle;
				}
			}

			if(options?.headerRowHeight && Number(options.headerRowHeight) > 0) {
				const headerHeight = Number(options.headerRowHeight);
				const existingRows = Array.isArray(ws['!rows']) ? [...ws['!rows']] : [];
				existingRows[0] = { ...(existingRows[0] || {}), hpt: headerHeight };
				ws['!rows'] = existingRows;
			}

			if(hasTotalsRow && totalsMergeSpan > 1) {
				if(!Array.isArray(ws['!merges'])) {
					ws['!merges'] = [];
				}
				ws['!merges'].push({
					s: { r: totalsRowOneBased - 1, c: 0 },
					e: { r: totalsRowOneBased - 1, c: totalsMergeSpan - 1 }
				});
			}
		}

		const wb: XLSX.WorkBook = XLSX.utils.book_new();

		XLSX.utils.book_append_sheet(wb, ws, sheetName);

		if(options?.negativeRedKeys && options.negativeRedKeys.length > 0) {
			const keyIndexMap = new Map<string, number>();
			dataKeys.forEach((key, idx) => keyIndexMap.set(key, idx));

			rows.forEach((row, rowIndex) => {
				options.negativeRedKeys!.forEach(key => {
					const keyIndex = keyIndexMap.get(key);
					if(keyIndex === undefined) {
						return;
					}
					const value = Number(row[key]);
					if(!isNaN(value) && value < 0) {
						const cellRef = `${toColName(keyIndex)}${rowIndex + 2}`;
						const cell = ws[cellRef];
						if(cell) {
							cell.s = {
								...(cell.s || {}),
								font: { ...(cell.s?.font || {}), color: { rgb: 'FF0000' } }
							};
						}
					}
				});
			});
		}

		if(options?.currencyKeys && options.currencyKeys.length > 0) {
			const keyIndexMap = new Map<string, number>();
			dataKeys.forEach((key, idx) => keyIndexMap.set(key, idx));

			rows.forEach((row, rowIndex) => {
				options.currencyKeys!.forEach(key => {
					const keyIndex = keyIndexMap.get(key);
					if(keyIndex === undefined) {
						return;
					}
					const value = Number(row?.[key]);
					if(isNaN(value)) {
						return;
					}
					const cellRef = `${toColName(keyIndex)}${rowIndex + 2}`;
					const cell = ws[cellRef];
					if(cell) {
						cell.s = {
							...(cell.s || {}),
							numFmt: moneyFormat
						};
					}
				});
			});
		}

		if(options?.negativeRowFillKey) {
			const fillKey = String(options.negativeRowFillKey || '').trim();
			const fillColor = normalizeColor(options?.negativeRowFillColor, 'FF0000');

			rows.forEach((row, rowIndex) => {
				if(hasTotalsRow && rowIndex === rows.length - 1) {
					return;
				}

				const value = Number(row?.[fillKey]);
				if(isNaN(value) || value >= 0) {
					return;
				}

				for(let colIndex = 0; colIndex < dataKeys.length; colIndex++) {
					const cellRef = `${toColName(colIndex)}${rowIndex + 2}`;
					const cell = ws[cellRef];
					if(cell) {
						cell.s = {
							...(cell.s || {}),
							fill: { patternType: 'solid', fgColor: { rgb: fillColor } }
						};
					}
				}
			});
		}


		if (detailsData && detailsData.length > 0) {
			const normalizedDetailsData = detailsData.map(row =>
				Array.isArray(row)
					? row.map(cell => this.toUpperCasePreservingEmail(cell))
					: row
			);
			const detailsWs: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(normalizedDetailsData);

			detailsWs['!cols'] = normalizedDetailsData[0]?.map((_, idx) => ({ wch: Math.max(20, ...normalizedDetailsData.map(row => (row[idx] ? String(row[idx]).length : 0))) })) || [];

			XLSX.utils.book_append_sheet(wb, detailsWs, detailsSheetName);
		}

		XLSX.writeFile(wb, `${fileName}.xlsx`);
	}

	public exportLiquidationPaymentReport(
		data: any[],
		displayHeaders: string[],
		dataKeys: string[],
		fileName: string,
		metadata: {
			expandedStart: string;
			expandedEnd: string;
			years: string;
			weeks: string;
			entities: string;
			totalPassagesBilling: number;
			totalToPay: number;
			totalToCollect: number;
		}
	): void {
		const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
		const normalizedDisplayHeaders = displayHeaders.map(header => String(header || '').toUpperCase());
		const normalizedData = data.map(row => {
			const normalizedRow: Record<string, any> = {};
			dataKeys.forEach(key => {
				const value = row?.[key];
				normalizedRow[key] = this.toUpperCasePreservingEmail(value);
			});
			return normalizedRow;
		});
		const normalizedMetadata = {
			...metadata,
			expandedStart: String(metadata.expandedStart || '').toUpperCase(),
			expandedEnd: String(metadata.expandedEnd || '').toUpperCase(),
			years: String(metadata.years || '').toUpperCase(),
			weeks: String(metadata.weeks || '').toUpperCase(),
			entities: String(metadata.entities || '').toUpperCase()
		};
		const lastCol = dataKeys.length - 1;
		const penultimateCol = lastCol - 1;
		const passagesKey = 'totalPassages';
		const depositKey = 'amountToDeposit';
		const totalsStartCol = 6;

		if(lastCol < 1) {
			return;
		}

		const toColName = (index: number): string => {
			let col = '';
			let n = index + 1;
			while(n > 0) {
				const rem = (n - 1) % 26;
				col = String.fromCharCode(65 + rem) + col;
				n = Math.floor((n - 1) / 26);
			}
			return col;
		};

		const cellRef = (row: number, col: number): string => `${toColName(col)}${row}`;

		const setCellValue = (row: number, col: number, value: any): void => {
			const normalizedValue = this.toUpperCasePreservingEmail(value);
			XLSX.utils.sheet_add_aoa(ws, [[normalizedValue]], { origin: cellRef(row, col) });
		};

		const setCellStyle = (row: number, col: number, style: any): void => {
			const ref = cellRef(row, col);
			if(!ws[ref]) {
				ws[ref] = { t: 's', v: '' };
			}
			ws[ref].s = style;
		};

		const moneyFormat = '"Q" #,##0.00;-"Q" #,##0.00';

		const styleTitle = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '3C78D8' } }
		};
		const styleHeaderBlue = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '1155CC' } }
		};
		const styleHeaderRed = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: 'FF0000' } }
		};
		const styleHeaderGreen = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '93C47D' } }
		};
		const styleCentered = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' }
		};
		const styleData = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'left', vertical: 'center' }
		};
		const styleBlueData = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'left', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '3C78D8' } }
		};
		const styleTotalsLabel = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' }
		};
		const styleTotalsCell = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' }
		};
		const styleTotalsBlueCell = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '3C78D8' } }
		};

		const titleValue = `Pago de liquidaciones semana ${normalizedMetadata.expandedStart} al ${normalizedMetadata.expandedEnd}`;
		setCellValue(1, 0, titleValue);

		setCellValue(2, penultimateCol, 'Total por pagar');
		setCellValue(3, penultimateCol, Number(normalizedMetadata.totalToPay) || 0);

		setCellValue(3, 2, 'Año');
		setCellValue(3, 3, normalizedMetadata.years || '');
		setCellValue(3, 4, 'Total Facturación de pasajes');
		setCellValue(4, 4, Number(normalizedMetadata.totalPassagesBilling) || 0);

		setCellValue(4, 2, 'Semana');
		setCellValue(4, 3, normalizedMetadata.weeks || '');
		setCellValue(5, 2, 'Entidad');
		setCellValue(5, 3, normalizedMetadata.entities || '');

		setCellValue(5, penultimateCol, 'Total por Cobrar');
		setCellValue(6, penultimateCol, Number(normalizedMetadata.totalToCollect) || 0);

		const totalsRow: any[] = Array.from({ length: dataKeys.length }, () => '');
		totalsRow[5] = 'Totales';

		for(let colIndex = totalsStartCol; colIndex <= lastCol; colIndex++) {
			const key = dataKeys[colIndex];
			const totalValue = normalizedData.reduce((sum, row) => {
				const numericValue = Number(row[key]);
				return sum + (isNaN(numericValue) ? 0 : numericValue);
			}, 0);
			totalsRow[colIndex] = totalValue;
		}

		XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: 'A9' });
		XLSX.utils.sheet_add_aoa(ws, [normalizedDisplayHeaders], { origin: 'A10' });
		XLSX.utils.sheet_add_json(ws, normalizedData, { origin: 'A11', skipHeader: true, header: dataKeys });

		const merges: XLSX.Range[] = [
			{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
			{ s: { r: 1, c: penultimateCol }, e: { r: 1, c: lastCol } },
			{ s: { r: 2, c: penultimateCol }, e: { r: 2, c: lastCol } },
			{ s: { r: 2, c: 4 }, e: { r: 2, c: 5 } },
			{ s: { r: 3, c: 4 }, e: { r: 3, c: 5 } },
			{ s: { r: 4, c: penultimateCol }, e: { r: 4, c: lastCol } },
			{ s: { r: 5, c: penultimateCol }, e: { r: 5, c: lastCol } }
		];
		ws['!merges'] = merges;

		setCellStyle(1, 0, styleTitle);
		setCellStyle(2, penultimateCol, styleHeaderRed);
		setCellStyle(3, penultimateCol, { ...styleCentered, numFmt: moneyFormat });
		setCellStyle(3, 2, styleHeaderGreen);
		setCellStyle(3, 3, styleCentered);
		setCellStyle(3, 4, styleHeaderRed);
		setCellStyle(4, 4, { ...styleCentered, numFmt: moneyFormat });
		setCellStyle(4, 2, styleHeaderGreen);
		setCellStyle(4, 3, styleCentered);
		setCellStyle(5, 2, styleHeaderGreen);
		setCellStyle(5, 3, styleCentered);
		setCellStyle(5, penultimateCol, styleHeaderRed);
		setCellStyle(6, penultimateCol, { ...styleCentered, numFmt: moneyFormat });

		setCellStyle(9, 5, styleTotalsLabel);
		for(let colIndex = totalsStartCol; colIndex <= lastCol; colIndex++) {
			const key = dataKeys[colIndex];
			const isBlueColumn = key === passagesKey || key === depositKey;
			setCellStyle(9, colIndex, {
				...(isBlueColumn ? styleTotalsBlueCell : styleTotalsCell),
				numFmt: moneyFormat
			});
		}

		for(let colIndex = 0; colIndex <= lastCol; colIndex++) {
			setCellStyle(10, colIndex, styleHeaderBlue);
		}

		for(let rowIndex = 0; rowIndex < normalizedData.length; rowIndex++) {
			const sheetRow = 11 + rowIndex;
			for(let colIndex = 0; colIndex <= lastCol; colIndex++) {
				const key = dataKeys[colIndex];
				const isBlueColumn = key === passagesKey || key === depositKey;
				const isMonetaryColumn = colIndex >= totalsStartCol;
				const baseStyle = isBlueColumn ? styleBlueData : styleData;
				setCellStyle(sheetRow, colIndex, isMonetaryColumn ? { ...baseStyle, numFmt: moneyFormat } : baseStyle);
			}
		}

		const totalsRowByKey: Record<string, any> = {};
		dataKeys.forEach((key, index) => {
			totalsRowByKey[key] = totalsRow[index];
		});
		const rowsForWidth: Record<string, any>[] = [totalsRowByKey, ...normalizedData];
		const colWidths = dataKeys.map((key, index) => {
			const headerLength = String(normalizedDisplayHeaders[index] || '').length;
			const maxDataLength = Math.max(
				0,
				...rowsForWidth.map(row => String(row?.[key] ?? '').length)
			);
			return { wch: Math.max(12, headerLength, maxDataLength) + 2 };
		});
		ws['!cols'] = colWidths;

		const wb: XLSX.WorkBook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Reporte pago liquidación');
		XLSX.writeFile(wb, `${fileName}.xlsx`);
	}

	public exportMissingDocumentsReport(
		blocks: Array<{
			groupKey: string;
			groupLabel: string;
			liquidations: Array<{
				liquidationId: string;
				liquidationLabel: string;
				isConsolidated?: boolean;
				mergedStatusText?: string;
				rows: Array<{
					partnerName: string;
					documentsPending: string;
					reason: string;
				}>;
			}>;
		}>,
		fileName: string,
		groupHeaderLabel: string,
		hideClientsColumn: boolean = false
	): void {
		const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
		const wb: XLSX.WorkBook = XLSX.utils.book_new();
		const headers = hideClientsColumn
			? ['NO.', groupHeaderLabel, 'LIQUIDACIÓN', 'DOCUMENTOS PENDIENTES', 'MOTIVO']
			: ['NO.', groupHeaderLabel, 'LIQUIDACIÓN', 'CLIENTES', 'DOCUMENTOS PENDIENTES', 'MOTIVO'];
		const lastCol = headers.length - 1;
		const merges: XLSX.Range[] = [];
		const headerFillColor = '93C47D';
		const blackBorder = {
			top: { style: 'thin', color: { rgb: '000000' } },
			bottom: { style: 'thin', color: { rgb: '000000' } },
			left: { style: 'thin', color: { rgb: '000000' } },
			right: { style: 'thin', color: { rgb: '000000' } }
		};

		const toColName = (index: number): string => {
			let col = '';
			let n = index + 1;
			while(n > 0) {
				const rem = (n - 1) % 26;
				col = String.fromCharCode(65 + rem) + col;
				n = Math.floor((n - 1) / 26);
			}
			return col;
		};

		const cellRef = (row: number, col: number): string => `${toColName(col)}${row}`;

		const ensureCell = (row: number, col: number, value: any = ''): XLSX.CellObject => {
			const ref = cellRef(row, col);
			if(!ws[ref]) {
				ws[ref] = { t: typeof value === 'number' ? 'n' : 's', v: value };
			}
			return ws[ref];
		};

		const setCellValue = (row: number, col: number, value: any): void => {
			const normalizedValue = this.toUpperCasePreservingEmail(value);
			XLSX.utils.sheet_add_aoa(ws, [[normalizedValue]], { origin: cellRef(row, col) });
		};

		const applyStyle = (row: number, col: number, style: any): void => {
			const cell = ensureCell(row, col);
			cell.s = {
				...(cell.s || {}),
				...style
			};
		};

		const centeredStyle = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			border: blackBorder
		};
		const leftStyle = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'left', vertical: 'center' },
			border: blackBorder
		};
		const headerStyle = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: headerFillColor } },
			border: blackBorder
		};
		const completeStyle = {
			font: { bold: true, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			border: blackBorder
		};

		XLSX.utils.sheet_add_aoa(ws, [headers.map(header => String(header || '').toUpperCase())], { origin: 'A1' });
		for(let colIndex = 0; colIndex <= lastCol; colIndex++) {
			applyStyle(1, colIndex, headerStyle);
		}

		let currentRow = 2;
		let blockIndex = 1;

		blocks.forEach(groupBlock => {
			const groupStartRow = currentRow;

			groupBlock.liquidations.forEach(liquidationBlock => {
				const liquidationStartRow = currentRow;
				const isConsolidatedRow = liquidationBlock.isConsolidated === true;
				const consolidatedStatusText = String(liquidationBlock.mergedStatusText || '').trim() || 'COMPLETA';
				const detailRows = isConsolidatedRow
					? [{ partnerName: '', documentsPending: consolidatedStatusText, reason: consolidatedStatusText }]
					: (Array.isArray(liquidationBlock.rows) ? liquidationBlock.rows : []);

				if(detailRows.length === 0) {
					return;
				}

				detailRows.forEach((detailRow, detailIndex) => {
					setCellValue(currentRow, 0, blockIndex);
					setCellValue(currentRow, 1, groupBlock.groupLabel || '');
					setCellValue(currentRow, 2, liquidationBlock.liquidationLabel || '');

					applyStyle(currentRow, 0, centeredStyle);
					applyStyle(currentRow, 1, centeredStyle);
					applyStyle(currentRow, 2, centeredStyle);

					if(isConsolidatedRow) {
						const completeStartCol = hideClientsColumn ? 3 : 3;
						const completeEndCol = hideClientsColumn ? 4 : 5;
						setCellValue(currentRow, completeStartCol, consolidatedStatusText);
						for(let colIndex = completeStartCol; colIndex <= completeEndCol; colIndex++) {
							applyStyle(currentRow, colIndex, completeStyle);
						}
						merges.push({
							s: { r: currentRow - 1, c: completeStartCol },
							e: { r: currentRow - 1, c: completeEndCol }
						});
					} else if(hideClientsColumn) {
						setCellValue(currentRow, 3, detailRow.documentsPending || '');
						setCellValue(currentRow, 4, detailRow.reason || '');
						applyStyle(currentRow, 3, centeredStyle);
						applyStyle(currentRow, 4, leftStyle);
					} else {
						setCellValue(currentRow, 3, detailRow.partnerName || '');
						setCellValue(currentRow, 4, detailRow.documentsPending || '');
						setCellValue(currentRow, 5, detailRow.reason || '');
						applyStyle(currentRow, 3, leftStyle);
						applyStyle(currentRow, 4, centeredStyle);
						applyStyle(currentRow, 5, leftStyle);
					}

					if(detailIndex === detailRows.length - 1 && detailRows.length > 1) {
						merges.push({
							s: { r: liquidationStartRow - 1, c: 0 },
							e: { r: currentRow - 1, c: 0 }
						});
						merges.push({
							s: { r: liquidationStartRow - 1, c: 2 },
							e: { r: currentRow - 1, c: 2 }
						});
					}

					currentRow += 1;
				});

				blockIndex += 1;
			});

			if(currentRow - groupStartRow > 1) {
				merges.push({
					s: { r: groupStartRow - 1, c: 1 },
					e: { r: currentRow - 2, c: 1 }
				});
			}
		});

		if(merges.length > 0) {
			ws['!merges'] = merges;
		}

		const totalRows = Math.max(currentRow - 1, 1);
		for(let rowIndex = 1; rowIndex <= totalRows; rowIndex++) {
			for(let colIndex = 0; colIndex <= lastCol; colIndex++) {
				ensureCell(rowIndex, colIndex);
				if(!ws[cellRef(rowIndex, colIndex)].s) {
					applyStyle(rowIndex, colIndex, rowIndex === 1 ? headerStyle : leftStyle);
				}
			}
		}

		const rowsForWidth: string[][] = [];
		rowsForWidth.push(headers.map(header => String(header || '').toUpperCase()));
		blocks.forEach(groupBlock => {
			groupBlock.liquidations.forEach(liquidationBlock => {
				const isConsolidatedRow = liquidationBlock.isConsolidated === true;
				const consolidatedStatusText = String(liquidationBlock.mergedStatusText || '').trim() || 'COMPLETA';
				if(isConsolidatedRow) {
					rowsForWidth.push(
						hideClientsColumn
							? ['1', String(groupBlock.groupLabel || ''), String(liquidationBlock.liquidationLabel || ''), consolidatedStatusText, consolidatedStatusText]
							: ['1', String(groupBlock.groupLabel || ''), String(liquidationBlock.liquidationLabel || ''), '', consolidatedStatusText, consolidatedStatusText]
					);
					return;
				}

				(liquidationBlock.rows || []).forEach(detailRow => {
					rowsForWidth.push(
						hideClientsColumn
							? [
								'1',
								String(groupBlock.groupLabel || ''),
								String(liquidationBlock.liquidationLabel || ''),
								String(detailRow.documentsPending || ''),
								String(detailRow.reason || '')
							]
							: [
								'1',
								String(groupBlock.groupLabel || ''),
								String(liquidationBlock.liquidationLabel || ''),
								String(detailRow.partnerName || ''),
								String(detailRow.documentsPending || ''),
								String(detailRow.reason || '')
							]
					);
				});
			});
		});

		ws['!cols'] = headers.map((header, headerIndex) => {
			const baseLength = String(header || '').length;
			const maxDataLength = Math.max(
				baseLength,
				...rowsForWidth.map(row => String(row[headerIndex] || '').length)
			);

			if(headerIndex === 0) return { wch: 8 };
			if(headerIndex === 2) return { wch: Math.max(28, maxDataLength + 2) };
			if((!hideClientsColumn && headerIndex === 3) || (hideClientsColumn && headerIndex === 4) || (!hideClientsColumn && headerIndex === 5)) {
				return { wch: Math.max(30, maxDataLength + 2) };
			}
			return { wch: Math.max(16, maxDataLength + 2) };
		});

		const existingRows = Array.isArray(ws['!rows']) ? [...ws['!rows']] : [];
		existingRows[0] = { ...(existingRows[0] || {}), hpt: 24 };
		ws['!rows'] = existingRows;

		XLSX.utils.book_append_sheet(wb, ws, 'Documentos Faltantes');
		XLSX.writeFile(wb, `${fileName}.xlsx`);
	}

	private sanitizeSheetName(name: string, usedNames: Set<string>): string {
		let sanitized = String(name || 'SOCIO').replace(/[\\/?*\[\]:]/g, ' ').trim();
		if(sanitized.length > 31) {
			sanitized = sanitized.substring(0, 31);
		}
		if(!sanitized) {
			sanitized = 'SOCIO';
		}

		let finalName = sanitized;
		let suffix = 1;
		while(usedNames.has(finalName)) {
			suffix += 1;
			const suffixText = ` (${suffix})`;
			finalName = sanitized.substring(0, 31 - suffixText.length) + suffixText;
		}
		usedNames.add(finalName);
		return finalName;
	}

	private buildRouteAccountStatementWorkbook(routeData: RouteAccountStatementRouteExport): XLSX.WorkBook {
		const moneyFormat = '"Q" #,##0.00;-"Q" #,##0.00';

		const styleTitle = {
			font: { bold: true, sz: 14, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '0070C0' } }
		};
		const styleHeader = {
			font: { bold: true, sz: 9, color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
			fill: { patternType: 'solid', fgColor: { rgb: '0070C0' } }
		};
		const styleDataCentered = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' }
		};
		const styleDataLeft = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'left', vertical: 'center' }
		};
		const styleFacturacion = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '5DD5FF' } }
		};
		const styleDeposit = {
			font: { color: { rgb: '000000' } },
			alignment: { horizontal: 'center', vertical: 'center' },
			fill: { patternType: 'solid', fgColor: { rgb: '4FFFA7' } }
		};

		const discountNames = routeData.discountNames || [];
		const discountCount = discountNames.length;
		const nitCol = 0;
		const socioCol = 1;
		const busesCol = 2;
		const facturacionCol = 3;
		const firstDiscountCol = 4;
		const totalDiscountsCol = firstDiscountCol + discountCount;
		const depositCol = totalDiscountsCol + 1;
		const paidCol = depositCol + 1;
		const labelCol = paidCol + 1;
		const lastCol = labelCol;

		const toColName = (index: number): string => {
			let col = '';
			let n = index + 1;
			while(n > 0) {
				const rem = (n - 1) % 26;
				col = String.fromCharCode(65 + rem) + col;
				n = Math.floor((n - 1) / 26);
			}
			return col;
		};
		const cellRef = (row: number, col: number): string => `${toColName(col)}${row}`;

		const wb: XLSX.WorkBook = XLSX.utils.book_new();
		const usedSheetNames = new Set<string>();

		for(const partner of routeData.partners) {
			const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);

			const setCellValue = (row: number, col: number, value: any): void => {
				XLSX.utils.sheet_add_aoa(ws, [[value]], { origin: cellRef(row, col) });
			};
			const setCellStyle = (row: number, col: number, style: any): void => {
				const ref = cellRef(row, col);
				if(!ws[ref]) {
					ws[ref] = { t: 's', v: '' };
				}
				ws[ref].s = style;
			};

			setCellValue(1, 0, `ESTADO DE CUENTA ${String(partner.partnerName || '').toUpperCase()}`);
			setCellStyle(1, 0, styleTitle);

			setCellValue(3, nitCol, 'NIT');
			setCellValue(3, socioCol, 'SOCIO');
			setCellValue(3, busesCol, 'AUTOBUSES');
			setCellValue(3, facturacionCol, 'FACTURACION PASAJES');
			setCellValue(3, firstDiscountCol, 'DESCUENTOS');
			setCellValue(3, totalDiscountsCol, 'TOTAL');

			discountNames.forEach((name, index) => {
				setCellValue(4, firstDiscountCol + index, name.toUpperCase());
			});
			setCellValue(4, totalDiscountsCol, 'DESCUENTOS');
			setCellValue(4, depositCol, 'DEPOSITAR');
			setCellValue(4, paidCol, 'PAGADO');
			setCellValue(4, labelCol, 'LIQUIDACION');

			const merges: XLSX.Range[] = [
				{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
				{ s: { r: 2, c: nitCol }, e: { r: 3, c: nitCol } },
				{ s: { r: 2, c: socioCol }, e: { r: 3, c: socioCol } },
				{ s: { r: 2, c: busesCol }, e: { r: 3, c: busesCol } },
				{ s: { r: 2, c: facturacionCol }, e: { r: 3, c: facturacionCol } }
			];

			if(discountCount > 0) {
				merges.push({ s: { r: 2, c: firstDiscountCol }, e: { r: 2, c: firstDiscountCol + discountCount - 1 } });
			}
			merges.push({ s: { r: 2, c: totalDiscountsCol }, e: { r: 2, c: labelCol } });

			for(let col = 0; col <= lastCol; col++) {
				setCellStyle(3, col, styleHeader);
				setCellStyle(4, col, styleHeader);
			}

			let rowIndex = 5;
			for(const week of partner.weeks) {
				const discountMap = new Map<string, number>();
				(week.discounts || []).forEach(discount => {
					discountMap.set(discount.name, Number(discount.amount) || 0);
				});

				setCellValue(rowIndex, nitCol, partner.nit || '');
				setCellValue(rowIndex, socioCol, partner.partnerName || '');
				setCellValue(rowIndex, busesCol, week.buses || '');
				setCellValue(rowIndex, facturacionCol, Number(week.facturacion) || 0);

				setCellStyle(rowIndex, nitCol, styleDataCentered);
				setCellStyle(rowIndex, socioCol, styleDataLeft);
				setCellStyle(rowIndex, busesCol, styleDataLeft);
				setCellStyle(rowIndex, facturacionCol, { ...styleFacturacion, numFmt: moneyFormat });

				discountNames.forEach((name, index) => {
					const col = firstDiscountCol + index;
					setCellValue(rowIndex, col, discountMap.get(name) || 0);
					setCellStyle(rowIndex, col, { ...styleDataCentered, numFmt: moneyFormat });
				});

				setCellValue(rowIndex, totalDiscountsCol, Number(week.totalDiscounts) || 0);
				setCellStyle(rowIndex, totalDiscountsCol, { ...styleDataCentered, numFmt: moneyFormat });

				setCellValue(rowIndex, depositCol, Number(week.deposit) || 0);
				setCellStyle(rowIndex, depositCol, { ...styleDeposit, numFmt: moneyFormat });

				setCellValue(rowIndex, paidCol, Number(week.paid) || 0);
				setCellStyle(rowIndex, paidCol, { ...styleDataCentered, numFmt: moneyFormat });

				setCellValue(rowIndex, labelCol, week.label || '');
				setCellStyle(rowIndex, labelCol, styleDataLeft);

				rowIndex += 1;
			}

			ws['!merges'] = merges;

			ws['!cols'] = Array.from({ length: lastCol + 1 }, (_, col) => {
				if(col === socioCol) return { wch: 32 };
				if(col === busesCol) return { wch: 20 };
				if(col === labelCol) return { wch: 30 };
				if(col >= firstDiscountCol && col <= totalDiscountsCol) return { wch: 18 };
				return { wch: 14 };
			});

			const sheetName = this.sanitizeSheetName(partner.partnerName, usedSheetNames);
			XLSX.utils.book_append_sheet(wb, ws, sheetName);
		}

		return wb;
	}

	public async exportRouteAccountStatementReport(
		routes: RouteAccountStatementRouteExport[],
		fileNamePrefix: string
	): Promise<void> {
		if(!Array.isArray(routes) || routes.length === 0) {
			return;
		}

		if(routes.length === 1) {
			const wb = this.buildRouteAccountStatementWorkbook(routes[0]);
			XLSX.writeFile(wb, `${fileNamePrefix}.xlsx`);
			return;
		}

		const zip = new JSZip();
		for(const routeData of routes) {
			const wb = this.buildRouteAccountStatementWorkbook(routeData);
			const wbArray = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
			const safeRouteName = String(routeData.routeName || routeData.routeId).replace(/[\\/?*:"<>|]/g, ' ').trim();
			zip.file(`${safeRouteName}.xlsx`, wbArray);
		}

		const zipBlob = await zip.generateAsync({ type: 'blob' });
		saveAs(zipBlob, `${fileNamePrefix}.zip`);
	}
}
